import { LoadingIndicator } from "@/components/LoadingIndicator";
import { PasswordResetLink } from "@/components/PasswordResetLink";
import { usePageLoading } from "@/contexts/page-loading.context";
import { useLoginTransition } from "@/hooks/useLoginTransition";
import {
  authFormClass,
  authPageContainerClass,
} from "@/layout/auth-layout.styles";
import { loginErrorMessage } from "@/utilities/login-error-message";
import { Button, Dialog, Progress, Typography } from "@iluvatar/ui-kit";

/* oxlint-disable no-console */
/* oxlint-disable @typescript-eslint/no-explicit-any */

import { optionalSearchString } from "@/utilities/search-params";
import {
  Link,
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import type { AuthError, UserCredential } from "firebase/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import * as Yup from "yup";

import { isErrorCode, timeout } from "@iluvatar/global/src/utilities";

import { TrustedComputerToggle } from "@/components/TrustedComputerToggle";
import { waitForAuthReady } from "@/services/auth-session";

import AppleSocialButton from "@/components/AppleLoginButton";
import { EmailField } from "@/components/Form/Fields/Email";
import { PasswordField } from "@/components/Form/Fields/Input";
import GoogleSocialButton from "@/components/GoogleLoginButton";
import { useAsyncAction } from "@/hooks/useAsync";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { getAuthRuntime } from "@/services/firebase-auth.runtime";
import requestService from "@/services/request.service";
import { localRedirect } from "@/utilities/navigation";

import {
  availableProviders,
  useAuthenticationContext,
} from "../contexts/authentication/authentication.context";
import { useFeatureFlags } from "../hooks/useFeatureFlags";

// styles

import { FormHelpers, RootFormProvider, useGroupErrors } from "@/FormLib";

function isSSOLinkError(err: unknown): err is SSOError {
  return Boolean(
    typeof err === "object" &&
    err &&
    Object.getOwnPropertyNames(err).includes("customData"),
  );
}

type LoginFormValue = {
  email: string;
  password: string;
  authForm: undefined;
};
type SSOError = {
  provider: "google.com" | "apple.com";
  message: string;
  customData?: {
    _tokenResponse: {
      oauthIdToken: string;
      providerId: string;
      verifiedProvider: string[];
    };
    email: string;
  };
};

const LOGIN_FORM_VALIDATOR = Yup.object().shape({
  email: Yup.string().required().email(),
  password: Yup.string().required(),
});

function LoginFormError() {
  const error = useGroupErrors("authForm");

  return error ? (
    <Typography role="alert" variant="body2" className="mb-4 text-uik-danger">
      {error}
    </Typography>
  ) : null;
}

function LoginPage(): JSX.Element {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  // Consume the URL marker without ending validation when the search updates.
  const [returningFromSSO] = useState(() => search.sso === "pending");
  const incomingSSOMarker = useRef(returningFromSSO);
  useEffect(() => {
    if (incomingSSOMarker.current && search.sso === "pending") {
      // A later marker belongs to an outgoing sign-in; Firebase still needs it.
      incomingSSOMarker.current = false;
      void navigate({
        to: "/login",
        search: (previous) => ({ ...previous, sso: undefined }),
        replace: true,
        resetScroll: false,
      });
    }
  }, [navigate, search.sso]);

  const { PUBLIC_REGISTRATION: publicRegistrationEnabled } = useFeatureFlags();
  const { login, ssoLogin, ssoRedirectHandler } = useAuthenticationContext();
  const [ssoError, setSSOError] = useState<SSOError | undefined>();
  const startSSOLogin = async (
    provider: SSOError["provider"],
    email?: string,
  ) => {
    setSSOError(undefined);
    setProcessingLogin(true);
    try {
      await ssoLogin(provider, email);
    } catch (error) {
      setSSOError({ provider, message: loginErrorMessage(error) });
      setProcessingLogin(false);
    }
  };

  const [ssoLinkError, setSSOLinkError] = useLocalStorage<AuthError | null>(
    "sso-login-link-error",
    null,
    { storage: "session" },
  );

  const [postLinkEscape, setPostLinkEscape] = useLocalStorage(
    "post-account-link-escape",
    false,
    { storage: "session" },
  );
  const [popUpVisible, setPopUpVisible] = useState<null | {
    providers?: string[];
    email?: string;
    message?: string;
    error?: string;
  }>(null);
  const [processingLogin, setProcessingLogin] = useState(false);

  const showApp = useCallback(() => {
    void navigate({ to: localRedirect(search.redirectTo) });
  }, [navigate, search.redirectTo]);

  const { setPhase } = useLoginTransition(showApp);

  const handleLoginSuccess = useCallback(async () => {
    setPostLinkEscape(false);
    setProcessingLogin(true);
    setPhase("success");
  }, [setPostLinkEscape, setPhase]);

  const loginFormSubmission = useCallback(
    async (
      { email, password }: LoginFormValue,
      helpers: FormHelpers<LoginFormValue>,
    ) => {
      setProcessingLogin(true);
      setPhase("submitting");
      try {
        setPostLinkEscape(false);
        await login(email, password);
        await handleLoginSuccess();
        return;
      } catch (error) {
        helpers.setFieldError("authForm", loginErrorMessage(error));
      }
      setPhase("idle");
      setProcessingLogin(false);
    },
    [handleLoginSuccess, login, setPostLinkEscape, setPhase],
  );

  /**
   * If the auth error provider is one of the available providers and the provider doesn't need verification
   * link the providers
   */
  const handleStandardSSOLinkError = useCallback(
    async (authError: AuthError, user: UserCredential) => {
      setSSOLinkError(null);
      const providerId = user.user.providerData.find((p) =>
        availableProviders.includes(p.providerId),
      );

      if (!providerId && user.user.providerData?.length) {
        throw new Error("Login provider you want to link is not supported");
      }

      const { OAuthProvider, linkWithCredential } = await getAuthRuntime();
      const credentials = OAuthProvider.credentialFromError(authError);
      if (!credentials) {
        throw new Error(
          "Error parsing credentials for authentication account linking",
        );
      }

      if ((authError.customData as any).needsConfirmation) {
        throw new Error(
          "Provider has not verified the email address, I cannot link this provider",
        );
      }
      await linkWithCredential(user?.user, credentials);
    },
    [setSSOLinkError],
  );

  const handleAuthAccountExistsWithDiffCredentialsError = useCallback(
    async (authError: AuthError) => {
      const { OAuthProvider } = await getAuthRuntime();
      const credentials = OAuthProvider.credentialFromError(authError);
      const tokenResponse = (authError?.customData as any)?._tokenResponse;

      if (!credentials || postLinkEscape) {
        setSSOError({
          message:
            "Error logging in / linking account providers. Contact support for help.",
          provider:
            (authError?.customData as any)?.provider || tokenResponse?.provider,
        });
        setPopUpVisible(null);
        setProcessingLogin(false);
        return;
      }

      /**
       * If theres a verified provider link through the standard flow
       */
      if (
        isSSOLinkError(authError) &&
        authError?.customData?._tokenResponse?.verifiedProvider &&
        authError?.customData?._tokenResponse?.verifiedProvider.length
      ) {
        setSSOLinkError(authError);
        setPopUpVisible({
          providers: authError.customData._tokenResponse.verifiedProvider,
          email: authError.customData.email,
          message:
            "Looks like you already have a login provider(s). \n Login with one of them and we'll connect em up.",
        });
        return null;
      }
      setPopUpVisible({});
      const rawUserIds = tokenResponse?.rawUserInfo
        ? JSON.parse(tokenResponse.rawUserInfo)
        : undefined;

      // Try to link providers
      await requestService
        .request("/api/link-account-provider", {
          method: "post",
          body: JSON.stringify({
            email: authError.customData.email,
            rawId: rawUserIds?.id,
            credentials: credentials?.toJSON(),
            displayName: rawUserIds?.displayName,
            federatedId: tokenResponse?.federatedId,
            authErrorToken: tokenResponse,
          }),
        })
        .catch((err) => {
          setPopUpVisible({
            error: loginErrorMessage(err),
          });
          throw err;
        });
      setPopUpVisible({
        message: "All Done! Were going to redirect you to the login again.",
      });
      setProcessingLogin(false);
      setPostLinkEscape(true);

      await timeout(2000);
      await ssoLogin(credentials.providerId, authError.customData.email);
      return null;
    },
    [postLinkEscape, setPostLinkEscape, setSSOLinkError, ssoLogin],
  );

  /**
   * On mount
   * - check if there are redirect results
   * - check if there is ssoLinkingError (link account)
   * - then login using the redirect user
   */
  const redirectCheck = useAsyncAction({
    queryKey: ["ssoRedirectHandler"],
    enabled: returningFromSSO,
    queryFn: async () => {
      setProcessingLogin(true);
      try {
        await waitForAuthReady();
        const { auth, getRedirectResult, browserPopupRedirectResolver } =
          await getAuthRuntime();
        const user = await getRedirectResult(
          auth,
          browserPopupRedirectResolver,
        );

        if (isSSOLinkError(ssoLinkError) && user?.user) {
          await handleStandardSSOLinkError(ssoLinkError, user);
        }

        if (user) {
          await ssoRedirectHandler(user).then(handleLoginSuccess);
          return true;
        }

        setProcessingLogin(false);
        return null;
      } catch (error) {
        const authError = error as AuthError;
        if (
          isErrorCode(
            authError,
            "auth/account-exists-with-different-credential",
          )
        ) {
          await handleAuthAccountExistsWithDiffCredentialsError(authError);
        } else {
          setSSOError({
            message: loginErrorMessage(authError),
            provider:
              (authError as any)?.customData?.provider ||
              (authError as any)?.provider ||
              (authError?.customData as any)?._tokenResponse?.provider,
          });
          setProcessingLogin(false);
          return null;
        }
      }
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const disabledPage =
    processingLogin ||
    (returningFromSSO && (redirectCheck.isPending || redirectCheck.isFetching));
  usePageLoading(disabledPage);

  return (
    <div
      aria-busy={disabledPage}
      className={`${authPageContainerClass} mx-auto`}
    >
      <div className="w-[170px] mt-[24px] justify-center align-center">
        <LoadingIndicator
          active={disabledPage}
          size={85}
          aria-label={disabledPage ? "Signing in" : "Bubble"}
          style={{ width: "100%" }}
        />
      </div>
      <Typography
        variant="h5"
        className={[
          "auth-title",
          "[color:rgb(var(--uik-subtle))] [margin-top:8px]",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        Sign In
      </Typography>

      <RootFormProvider<LoginFormValue>
        id="login-submit"
        disabled={disabledPage}
        initialValues={{
          password: "",
          email: "",
          authForm: undefined,
        }}
        onSubmit={loginFormSubmission}
        validationSchema={LOGIN_FORM_VALIDATOR}
        disabledEnterNextFieldNavigation
        className={`${authFormClass}`}
      >
        <EmailField
          name="email"
          label="Email"
          disabled={disabledPage}
          className="auth-form-element"
        />
        <PasswordField
          name="password"
          label="Password"
          disabled={disabledPage}
          className="auth-form-element"
        />
        <div className="auth-login-options">
          <TrustedComputerToggle disabled={disabledPage} />
          <PasswordResetLink disabled={disabledPage} />
        </div>
        <LoginFormError />
        <Button
          type="submit"
          variant="outline"
          disabled={disabledPage}
          className={[
            "auth-form-element auth-button bg-[var(--color-background-paper)] !m-0",
            "w-full",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          Sign In
        </Button>
      </RootFormProvider>

      <div className="mb-4 w-full max-w-[450px] px-6">
        <div className="w-full flex items-center my-6">
          <span className="border border-solid border-b-[var(--color-border)] border-[transparent] flex-1 mr-3 opacity-[0.4]" />
          or
          <span className="border border-solid border-b-[var(--color-border)] border-[transparent] flex-1 ml-3 opacity-[0.4]" />
        </div>

        {!ssoError?.provider && ssoError?.message && (
          <Typography
            variant="caption"
            className="[color:rgb(var(--uik-danger))] [padding:16px]"
          >
            {ssoError.message}
          </Typography>
        )}

        <GoogleSocialButton
          disabled={disabledPage}
          onClick={() => {
            setPostLinkEscape(false);
            void startSSOLogin("google.com");
          }}
        />
        {ssoError?.provider === "google.com" && (
          <Typography
            variant="caption"
            className="[color:rgb(var(--uik-danger))] [padding:16px]"
          >
            {ssoError.message}
          </Typography>
        )}

        <AppleSocialButton
          disabled={disabledPage}
          onClick={() => {
            setPostLinkEscape(false);
            void startSSOLogin("apple.com");
          }}
          className="mt-5"
        />
        {ssoError?.provider === "apple.com" && (
          <Typography
            variant="caption"
            className="[color:rgb(var(--uik-danger))] [padding:16px]"
          >
            {ssoError.message}
          </Typography>
        )}
      </div>

      {publicRegistrationEnabled && (
        <Link
          to="/register"
          disabled={disabledPage}
          className="auth-secondary-link text-[var(--color-text--secondary)] mb-7"
        >
          Create an account
        </Link>
      )}

      {/* <div className="mb-3 w-[450px] px-6 text-[13px]">
          <div className="w-full flex flex-column items-center mb-1">
            <span className="border border-solid border-b-[var(--color-border)] border-[transparent] flex-1 opacity-[0.4]" />
          </div>
          <div className="w-full flex justify-center gap-16 mt-4">
            <a
              href="/about?intro=Aboot"
              className="text-center color-[var(--color-text--secondary)]"
              rel="noreferrer"
            >
              about
            </a>
          </div>
        </div> */}
      <Dialog
        open={Boolean(popUpVisible)}
        onOpenChange={(next) => {
          if (!next) {
            (() => setPopUpVisible(null))();
          }
        }}
        title="Linking your account"
      >
        <div className="">
          <div className="mb-4">
            {!popUpVisible?.message && !popUpVisible?.error && (
              <>
                <Typography className="[text-align:center]">
                  This email already has a login method associated with an
                  account.
                </Typography>
                <Typography className="[text-align:center]">
                  We are attempting to link this new one.
                </Typography>
              </>
            )}
            {popUpVisible?.message && (
              <Typography className="[text-align:center]">
                {popUpVisible?.message}
              </Typography>
            )}
            {popUpVisible?.error && (
              <Typography className="[text-align:center]">
                {popUpVisible?.error}
              </Typography>
            )}
            {popUpVisible?.providers?.includes("password") && (
              <RootFormProvider<LoginFormValue>
                id="auth-form"
                disabled={disabledPage}
                initialValues={{
                  password: "",
                  email: popUpVisible.email || "",
                  authForm: undefined,
                }}
                onSubmit={loginFormSubmission}
                validationSchema={LOGIN_FORM_VALIDATOR}
                disabledEnterNextFieldNavigation
                className={`${authFormClass}`}
              >
                <EmailField
                  disabled={disabledPage || Boolean(popUpVisible.email)}
                  name="email"
                  label="Email"
                  className="auth-form-element"
                />
                <PasswordField
                  name="password"
                  label="Password"
                  disabled={disabledPage}
                  className="auth-form-element"
                />
                <LoginFormError />
                <Button
                  form="auth-form"
                  type="submit"
                  variant="outline"
                  disabled={disabledPage}
                  className={[
                    "auth-form-element auth-button bg-[var(--color-background-paper)] !m-0",
                    "w-full",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  Sign In
                </Button>
              </RootFormProvider>
            )}

            {popUpVisible?.providers?.includes("google.com") && (
              <div className="mt-4" />
            )}
            {popUpVisible?.providers?.includes("google.com") && (
              <GoogleSocialButton
                disabled={disabledPage}
                onClick={() => {
                  setPostLinkEscape(false);
                  void startSSOLogin("google.com", popUpVisible.email);
                }}
              />
            )}
          </div>
        </div>
        {disabledPage ? (
          <div className="flex justify-center p-3">
            <LoadingIndicator aria-label="Signing in" />
          </div>
        ) : (
          <Progress label="Progress" value={100} />
        )}
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>) => ({
    ...optionalSearchString("redirectTo")(search),
    ...optionalSearchString("sso")(search),
  }),
  component: LoginPage,
});
